import { DomainValidationError } from "./identifiers.js";
import { ianaTimeZone, localDateTimeOf } from "./time-zone.js";
/** A customer with no saved row reads as these settings, at version 1. The first save inserts version 2. */
export const DEFAULT_PUSH_SETTINGS = Object.freeze({
    pushEnabled: true,
    tripUpdates: true,
    quietHours: null,
    timeZone: null,
});
export const PUSH_SETTINGS_FIRST_VERSION = 1;
export const pushSettingFields = Object.freeze([
    "PUSH_ENABLED",
    "TRIP_UPDATES",
    "QUIET_HOURS",
    "TIME_ZONE",
]);
const fail = (field, rule) => new DomainValidationError(field, rule);
const isMinute = (value) => typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 1439;
/**
 * Reads the settings a client sends. Every field is required (no default is guessed). Quiet hours need a zone; the two ends must differ
 * (an equal pair would mean "never" or "always"). The message names the field and the rule, never the value.
 */
export function validatePushSettings(input) {
    if (typeof input !== "object" || input === null)
        throw fail("settings", "must be an object");
    const record = input;
    if (typeof record.pushEnabled !== "boolean")
        throw fail("pushEnabled", "must be true or false");
    if (typeof record.tripUpdates !== "boolean")
        throw fail("tripUpdates", "must be true or false");
    let quietHours = null;
    if (record.quietHours !== null && record.quietHours !== undefined) {
        const hours = record.quietHours;
        if (typeof hours !== "object" || !isMinute(hours.startMinute) || !isMinute(hours.endMinute)) {
            throw fail("quietHours", "must give two minutes of the day, 0 to 1439");
        }
        if (hours.startMinute === hours.endMinute) {
            throw fail("quietHours", "must start and end at different times");
        }
        quietHours = Object.freeze({ startMinute: hours.startMinute, endMinute: hours.endMinute });
    }
    const timeZone = record.timeZone === null || record.timeZone === undefined
        ? null
        : ianaTimeZone(record.timeZone, "timeZone");
    if (quietHours !== null && timeZone === null) {
        throw fail("timeZone", "is required when quiet hours are set");
    }
    return Object.freeze({
        pushEnabled: record.pushEnabled,
        tripUpdates: record.tripUpdates,
        quietHours,
        timeZone,
    });
}
/** The fields that differ between two settings, as codes. */
export function changedPushSettings(current, next) {
    const changed = [];
    if (current.pushEnabled !== next.pushEnabled)
        changed.push("PUSH_ENABLED");
    if (current.tripUpdates !== next.tripUpdates)
        changed.push("TRIP_UPDATES");
    if (current.quietHours?.startMinute !== next.quietHours?.startMinute ||
        current.quietHours?.endMinute !== next.quietHours?.endMinute) {
        changed.push("QUIET_HOURS");
    }
    if (current.timeZone !== next.timeZone)
        changed.push("TIME_ZONE");
    return changed;
}
const minuteOfDay = (now, zone) => {
    const local = localDateTimeOf(now, zone); // YYYY-MM-DDTHH:MM
    return Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16));
};
/** Minutes left of the quiet window at `now`, or `0` when `now` is outside it (or no window is set). */
export function quietMinutesRemaining(settings, now) {
    if (settings.quietHours === null || settings.timeZone === null)
        return 0;
    const { startMinute, endMinute } = settings.quietHours;
    const current = minuteOfDay(now, settings.timeZone);
    const inside = startMinute < endMinute
        ? current >= startMinute && current < endMinute
        : current >= startMinute || current < endMinute;
    if (!inside)
        return 0;
    return (endMinute - current + 1440) % 1440 || 1440;
}
// ---- What each outbox kind becomes -------------------------------------------------------------
export const notificationClasses = Object.freeze(["TRANSACTIONAL", "OPTIONAL"]);
export const pushTitleKeys = Object.freeze([
    "REQUEST_NEEDS_ANSWER",
    "REQUEST_REMINDER",
    "REQUEST_CONFIRMED",
    "REQUEST_UPDATE",
    "PAYMENT_REQUIRED",
    "PAYMENT_EXPIRED",
    "PAYMENT_RECEIVED",
    "TRIP_UPCOMING",
    "TRIP_WEATHER_CHANGE",
    "STAFF_ATTENTION",
]);
/** Where a push opens. The link is built from the SUBJECT of the row, never from text. */
export const pushLinkKinds = Object.freeze([
    "REQUEST_ACTION",
    "PAYMENT_ACTION",
    "TRIP",
    "TRIP_OF_ITEM",
    "STAFF_QUEUE",
]);
const spec = (cls, titleKey, link) => Object.freeze({ class: cls, titleKey, link });
/**
 * The kinds that become a push, by audience. A kind or an audience that is not listed is NOT a push (a submitted request, an assignment,
 * a customer's own cancel). The staff rows have a spec so that the policy is the same for every audience, but the mobile app is for
 * customers: a staff push needs a staff device binding, and none exists in this release.
 */
const DELIVERY_SPECS = Object.freeze({
    REQUEST_AWAITING_CUSTOMER: {
        CUSTOMER: spec("TRANSACTIONAL", "REQUEST_NEEDS_ANSWER", "REQUEST_ACTION"),
    },
    REQUEST_CUSTOMER_REMINDER: {
        CUSTOMER: spec("TRANSACTIONAL", "REQUEST_REMINDER", "REQUEST_ACTION"),
    },
    REQUEST_PAYMENT_REQUIRED: {
        CUSTOMER: spec("TRANSACTIONAL", "PAYMENT_REQUIRED", "REQUEST_ACTION"),
    },
    REQUEST_CONFIRMED: { CUSTOMER: spec("TRANSACTIONAL", "REQUEST_CONFIRMED", "REQUEST_ACTION") },
    REQUEST_UNAVAILABLE: { CUSTOMER: spec("TRANSACTIONAL", "REQUEST_UPDATE", "REQUEST_ACTION") },
    REQUEST_COMPLETED: { CUSTOMER: spec("TRANSACTIONAL", "REQUEST_UPDATE", "REQUEST_ACTION") },
    REQUEST_DECLINED: { CUSTOMER: spec("TRANSACTIONAL", "REQUEST_UPDATE", "REQUEST_ACTION") },
    REQUEST_EXPIRED: { CUSTOMER: spec("TRANSACTIONAL", "REQUEST_UPDATE", "REQUEST_ACTION") },
    PAYMENT_EXPIRED: { CUSTOMER: spec("TRANSACTIONAL", "PAYMENT_EXPIRED", "PAYMENT_ACTION") },
    PAYMENT_PAID: { CUSTOMER: spec("TRANSACTIONAL", "PAYMENT_RECEIVED", "PAYMENT_ACTION") },
    REQUEST_SLA_ESCALATED: { STAFF: spec("TRANSACTIONAL", "STAFF_ATTENTION", "STAFF_QUEUE") },
    TRIP_UPCOMING: { CUSTOMER: spec("OPTIONAL", "TRIP_UPCOMING", "TRIP") },
    ITINERARY_WEATHER_RISK: {
        CUSTOMER: spec("OPTIONAL", "TRIP_WEATHER_CHANGE", "TRIP_OF_ITEM"),
        STAFF: spec("TRANSACTIONAL", "STAFF_ATTENTION", "STAFF_QUEUE"),
    },
    TRIP_ATTENTION_NEEDED: { STAFF: spec("TRANSACTIONAL", "STAFF_ATTENTION", "STAFF_QUEUE") },
});
/** The delivery spec of an outbox kind for an audience, or `null` when that row is not a push. */
export function deliverySpecOf(kind, audience) {
    return DELIVERY_SPECS[kind]?.[audience] ?? null;
}
/** Every kind that has a spec (a test asserts that each lifecycle kind and each request kind is decided on purpose). */
export const deliveryKinds = Object.freeze(Object.keys(DELIVERY_SPECS));
// ---- The decision ---------------------------------------------------------------------------------
/** What the policy says about one row. Closed codes: they go into the delivery record and the task decision. */
export const suppressionReasons = Object.freeze([
    "NO_PUSH_FOR_KIND",
    "PUSH_OFF",
    "CATEGORY_OFF",
    "NO_CHANNEL",
    "PERMISSION_DENIED",
    "RATE_LIMITED",
    "STALE",
    "SUBJECT_CHANGED",
    "IDENTITY_REVOKED",
    "DUPLICATE",
    "PROVIDER_OFF",
]);
export const DEFAULT_DELIVERY_LIMITS = Object.freeze({
    maxAgeMs: Object.freeze({ TRANSACTIONAL: 24 * 3_600_000, OPTIONAL: 12 * 3_600_000 }),
    perWindow: Object.freeze({ TRANSACTIONAL: 20, OPTIONAL: 3 }),
    windowMs: Object.freeze({ TRANSACTIONAL: 3_600_000, OPTIONAL: 24 * 3_600_000 }),
});
/**
 * Send, defer or suppress. The order is fixed and each step is a reason a test can reach:
 * not a push -> global off -> category off -> no channel -> too old -> over the limit -> quiet hours (defer) -> send.
 */
export function decideDelivery(facts) {
    const limits = facts.limits ?? DEFAULT_DELIVERY_LIMITS;
    const suppress = (reason) => ({
        action: "SUPPRESS",
        reason,
    });
    if (facts.spec === null)
        return suppress("NO_PUSH_FOR_KIND");
    const cls = facts.spec.class;
    const settings = facts.settings;
    if (settings !== null && !settings.pushEnabled)
        return suppress("PUSH_OFF");
    if (settings !== null && cls === "OPTIONAL" && !settings.tripUpdates) {
        return suppress("CATEGORY_OFF");
    }
    if (facts.channel === "NONE")
        return suppress("NO_CHANNEL");
    if (facts.channel === "PERMISSION_DENIED")
        return suppress("PERMISSION_DENIED");
    if (facts.now.getTime() - facts.createdAt.getTime() > limits.maxAgeMs[cls]) {
        return suppress("STALE");
    }
    if (facts.recentSent[cls] >= limits.perWindow[cls])
        return suppress("RATE_LIMITED");
    if (settings !== null && cls === "OPTIONAL") {
        const wait = quietMinutesRemaining(settings, facts.now);
        if (wait > 0) {
            return { action: "DEFER", until: new Date(facts.now.getTime() + wait * 60_000) };
        }
    }
    return { action: "SEND" };
}
// ---- The payload: closed templates, an opaque link, a scanner ---------------------------------------
/**
 * The words of a push. One closed template per title key, no variable part: no name, no message text, no amount, no place, no date.
 * The title says that something needs the customer; the app shows the detail after the sign-in and the owner check.
 */
export const PUSH_TEMPLATES = Object.freeze({
    REQUEST_NEEDS_ANSWER: {
        title: "Your concierge has a question",
        body: "Open Lafayette to reply.",
    },
    REQUEST_REMINDER: { title: "Your concierge is waiting", body: "Open Lafayette to reply." },
    REQUEST_CONFIRMED: {
        title: "Your request is confirmed",
        body: "Open Lafayette for the details.",
    },
    REQUEST_UPDATE: { title: "An update on your request", body: "Open Lafayette for the details." },
    PAYMENT_REQUIRED: { title: "A payment is needed", body: "Open Lafayette to review it." },
    PAYMENT_EXPIRED: {
        title: "A payment request has ended",
        body: "Open Lafayette to see what is next.",
    },
    PAYMENT_RECEIVED: { title: "A payment was received", body: "Open Lafayette for the details." },
    TRIP_UPCOMING: { title: "Your trip is coming up", body: "Open Lafayette to review your plan." },
    TRIP_WEATHER_CHANGE: {
        title: "The weather may affect your plan",
        body: "Open Lafayette to see what changed.",
    },
    STAFF_ATTENTION: { title: "Something needs attention", body: "Open the staff queue." },
});
const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
/**
 * The only links a push may carry: the scheme of the app, a fixed root, ids in the exact UUID form, no query and no fragment. Staff
 * rows link to the queue root. The mobile resolver (`parseDeepLink`) reads the same grammar.
 */
export const PUSH_LINK_PATTERN = new RegExp(`^lafayette://(?:trips/${UUID}|actions/(?:concierge_request|payment_request):${UUID}|profile|concierge)$`);
/** The link of a push. `null` when the id is not a UUID or the kind has no customer link. Pure string work: nothing is read. */
export function pushLinkOf(link, ids) {
    const id = new RegExp(`^${UUID}$`);
    const lower = (value) => value.toLowerCase();
    switch (link) {
        case "REQUEST_ACTION":
            return id.test(lower(ids.subjectId))
                ? `lafayette://actions/concierge_request:${lower(ids.subjectId)}`
                : null;
        case "PAYMENT_ACTION":
            return id.test(lower(ids.subjectId))
                ? `lafayette://actions/payment_request:${lower(ids.subjectId)}`
                : null;
        case "TRIP":
            return id.test(lower(ids.subjectId)) ? `lafayette://trips/${lower(ids.subjectId)}` : null;
        case "TRIP_OF_ITEM":
            return ids.tripId !== undefined && ids.tripId !== null && id.test(lower(ids.tripId))
                ? `lafayette://trips/${lower(ids.tripId)}`
                : null;
        case "STAFF_QUEUE":
            return "lafayette://concierge";
    }
}
export function buildPushPayload(titleKey, url) {
    const template = PUSH_TEMPLATES[titleKey];
    const payload = Object.freeze({
        titleKey,
        title: template.title,
        body: template.body,
        data: Object.freeze({ lf: 1, url }),
    });
    const violations = scanPushPayload(payload);
    if (violations.length > 0) {
        throw new RangeError(`a push payload was refused: ${violations.join(",")}`);
    }
    return payload;
}
export const pushPayloadViolations = Object.freeze([
    "NOT_AN_OBJECT",
    "UNKNOWN_KEY",
    "UNKNOWN_TITLE_KEY",
    "TEXT_NOT_FROM_TEMPLATE",
    "SENSITIVE_TEXT",
    "DATA_SHAPE",
    "LINK_NOT_ALLOWED",
]);
/**
 * Words and symbols that a lock screen text must never hold: a digit (an amount, a date, a count), an `@`, a currency sign or code, a
 * web address, a quote (a quoted message). The templates hold none. A future edit of a template that adds one fails the scan of every
 * template (a test) and the scan of every payload (this function).
 */
const SENSITIVE_TEXT = /[0-9@"“”]|[₹$€£¥]|\b(?:INR|USD|EUR|GBP)\b|https?:|www\./iu;
/** `true` when a text that goes on a lock screen holds something sensitive. */
export const textIsSensitive = (text) => SENSITIVE_TEXT.test(text);
/**
 * Checks an outgoing payload: the exact keys, the title key from the closed list, the words equal to the template (so a name or a message
 * text cannot be put in), no sensitive pattern, the data exactly `{ lf: 1, url }` and the url a link of the allowed grammar. Returns the
 * violation codes (empty: the payload is clean). The delivery task runs it on every payload before the provider sees it.
 */
export function scanPushPayload(payload) {
    if (typeof payload !== "object" || payload === null || Array.isArray(payload)) {
        return ["NOT_AN_OBJECT"];
    }
    const found = new Set();
    const record = payload;
    const allowed = ["titleKey", "title", "body", "data"];
    if (Object.keys(record).some((key) => !allowed.includes(key)))
        found.add("UNKNOWN_KEY");
    const { titleKey, title, body, data } = record;
    const template = typeof titleKey === "string" && pushTitleKeys.includes(titleKey)
        ? PUSH_TEMPLATES[titleKey]
        : null;
    if (template === null)
        found.add("UNKNOWN_TITLE_KEY");
    for (const [text, expected] of [
        [title, template?.title],
        [body, template?.body],
    ]) {
        if (typeof text !== "string") {
            found.add("TEXT_NOT_FROM_TEMPLATE");
            continue;
        }
        if (textIsSensitive(text))
            found.add("SENSITIVE_TEXT");
        if (expected === undefined || text !== expected)
            found.add("TEXT_NOT_FROM_TEMPLATE");
    }
    if (typeof data !== "object" || data === null || Array.isArray(data)) {
        found.add("DATA_SHAPE");
    }
    else {
        const shape = data;
        const keys = Object.keys(shape).sort();
        if (keys.length !== 2 || keys[0] !== "lf" || keys[1] !== "url" || shape.lf !== 1) {
            found.add("DATA_SHAPE");
        }
        if (typeof shape.url !== "string" || !PUSH_LINK_PATTERN.test(shape.url)) {
            found.add("LINK_NOT_ALLOWED");
        }
    }
    return [...found];
}
// ---- Identity and device binding vocabulary -----------------------------------------------------------
export const notificationOwnerTypes = Object.freeze(["CUSTOMER", "STAFF"]);
export const notificationPlatforms = Object.freeze(["IOS", "ANDROID"]);
/** What the device says about the OS permission to show a push. `NOT_ASKED` is a device that has not shown the system prompt yet. */
export const notificationPermissions = Object.freeze(["GRANTED", "DENIED", "NOT_ASKED"]);
/** A random id the app makes once per install. It names the device, never the person. */
export const INSTALLATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;
/**
 * The shape of an Expo push token: `ExponentPushToken[...]` (the form of the Expo documentation) or `ExpoPushToken[...]` (the second form
 * that the Expo server SDK accepts). The inner part is a short run of letters, digits, dash and underscore. A token is a secret of the
 * device: it never enters a log, an event, an audit row, an export file or an API answer (`redactString` knows this shape).
 */
export const EXPO_PUSH_TOKEN_PATTERN = /^Expo(?:nent)?PushToken\[[A-Za-z0-9_-]{8,64}\]$/;
/** The state of a stored push token. Only `ACTIVE` holds the token value: an ended token is erased from the row. */
export const pushTokenStates = Object.freeze(["ACTIVE", "INVALID", "UNBOUND"]);
/** Why a token ended. `DEVICE_NOT_REGISTERED` is the only reason of `INVALID`; every other reason is an `UNBOUND` token. */
export const pushTokenEndReasons = Object.freeze([
    "SIGN_OUT",
    "ACCOUNT_DELETED",
    "STAFF_OFFBOARDED",
    "ACCOUNT_SWITCH",
    "TOKEN_REPLACED",
    "DEVICE_NOT_REGISTERED",
]);
/**
 * What Expo answers for one message of a send call: a TICKET. `OK` means that Expo accepted the message (it holds a ticket id). It does not
 * mean that a device showed it: that is the receipt, read later.
 */
export const pushTicketStatuses = Object.freeze(["OK", "ERROR"]);
/** The receipt of a ticket, read 15 minutes or more after the send. `MISSING`: Expo had no receipt when the checks ran out. */
export const pushReceiptStatuses = Object.freeze(["OK", "ERROR", "MISSING"]);
/** A provider error code on a ticket or a receipt (`DeviceNotRegistered`, `MessageRateExceeded`, ...). A short identifier, never free text. */
export const PUSH_PROVIDER_ERROR_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
/** The one Expo error that ends a token: the device cannot receive any more messages with it. */
export const PUSH_DEVICE_NOT_REGISTERED = "DeviceNotRegistered";
/**
 * The CLOSED codes an operator sees for a receipt that failed (CHT-764 review fix 8). Expo's own error names map to one of these; any other
 * name (the text is not ours) becomes `OTHER`. A ticket whose receipt never came is `MISSING`. The operator view never shows provider text,
 * a ticket id or a token.
 */
export const pushReceiptFailureCodes = Object.freeze([
    "DEVICE_NOT_REGISTERED",
    "MESSAGE_TOO_BIG",
    "MESSAGE_RATE_EXCEEDED",
    "INVALID_CREDENTIALS",
    "MISMATCH_SENDER_ID",
    "MISSING",
    "OTHER",
]);
const RECEIPT_FAILURE_BY_PROVIDER_NAME = {
    DeviceNotRegistered: "DEVICE_NOT_REGISTERED",
    MessageTooBig: "MESSAGE_TOO_BIG",
    MessageRateExceeded: "MESSAGE_RATE_EXCEEDED",
    InvalidCredentials: "INVALID_CREDENTIALS",
    MismatchSenderId: "MISMATCH_SENDER_ID",
};
/** Maps the receipt of one ticket to a closed failure code, or `null` when the receipt is fine (or not due yet). */
export function receiptFailureCodeOf(receiptStatus, receiptErrorCode) {
    if (receiptStatus === "MISSING")
        return "MISSING";
    if (receiptStatus !== "ERROR")
        return null;
    return ((receiptErrorCode !== null &&
        Object.hasOwn(RECEIPT_FAILURE_BY_PROVIDER_NAME, receiptErrorCode) &&
        RECEIPT_FAILURE_BY_PROVIDER_NAME[receiptErrorCode]) ||
        "OTHER");
}
/** The shape of a session key: 16 to 64 letters, digits, dash or underscore (the app makes 32 random hex characters). */
export const DEVICE_SESSION_KEY_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;
/** The key of a session for the calls that only close it (a sign-out). `null` when the app sent none. Never echoes a bad value. */
export function validateDeviceSessionKey(value) {
    if (value === undefined || value === null)
        return null;
    if (typeof value !== "string" || !DEVICE_SESSION_KEY_PATTERN.test(value)) {
        throw fail("sessionKey", "must be 16 to 64 letters, digits, dash or underscore, or absent");
    }
    return value;
}
export function validateDeviceBinding(input) {
    if (typeof input !== "object" || input === null)
        throw fail("binding", "must be an object");
    const record = input;
    if (typeof record.installationId !== "string" ||
        !INSTALLATION_ID_PATTERN.test(record.installationId)) {
        throw fail("installationId", "must be 16 to 64 letters, digits, dash or underscore");
    }
    if (!notificationPlatforms.includes(record.platform)) {
        throw fail("platform", "must be IOS or ANDROID");
    }
    if (!notificationPermissions.includes(record.permission)) {
        throw fail("permission", "must be GRANTED, DENIED or NOT_ASKED");
    }
    const pushToken = record.pushToken ?? null;
    if (pushToken !== null &&
        (typeof pushToken !== "string" || !EXPO_PUSH_TOKEN_PATTERN.test(pushToken))) {
        // The message never echoes the value: it may be a token.
        throw fail("pushToken", "must be an Expo push token or absent");
    }
    return Object.freeze({
        installationId: record.installationId,
        platform: record.platform,
        permission: record.permission,
        pushToken,
        sessionKey: validateDeviceSessionKey(record.sessionKey),
    });
}
/** Why an identity ended. Closed codes for the audit row and the revoke call. */
export const identityRevokeReasons = Object.freeze([
    "SIGN_OUT",
    "ACCOUNT_DELETED",
    "STAFF_OFFBOARDED",
    "ACCOUNT_SWITCH",
]);
/** The record of one delivery decision. Written once per outbox row, never changed. */
export const deliveryOutcomes = Object.freeze(["SENT", "SUPPRESSED", "FAILED"]);
/** Why a send failed, as a closed code (`RETRY_EXHAUSTED` is written when the attempts run out). */
export const deliveryFailureCodes = Object.freeze([
    "RETRY_EXHAUSTED",
    "PROVIDER_REJECTED",
    "PAYLOAD_REFUSED",
    "LINK_UNAVAILABLE",
    // The provider has no idempotency key (Expo push). A send whose answer was lost is not retried when a retry could show the message twice
    // (docs/engineering/lifecycle-notifications.md, section 8). An operator can re-run the row.
    "RESULT_UNKNOWN",
]);
/** Every reason code a delivery record may hold. */
export const deliveryReasonCodes = Object.freeze([
    ...suppressionReasons,
    ...deliveryFailureCodes,
]);
